import { isStaticBuilderField, isStatusField } from './formStudioFormUtils';
import { normalizeFieldOptions } from './fieldOptionsUtils';

export const SETUP_STEP_KEYS = Object.freeze([
  'overview',
  'fields',
  'automations',
]);

/** @typedef {'complete'|'current'|'pending'|'upcoming'|'optional'} SetupStepState */

/**
 * @param {Array<object>|undefined|null} fields
 */
export function hasCustomFormFields(fields) {
  return (fields || []).some((field) => !isStaticBuilderField(field));
}

/**
 * @param {Array<object>|undefined|null} fields
 */
export function formHasStatusField(fields) {
  return (fields || []).some(isStatusField);
}

/**
 * Status fields are configured when they have at least one option.
 *
 * @param {Array<object>|undefined|null} fields
 */
export function statusFieldsConfigured(fields) {
  const statusFields = (fields || []).filter(isStatusField);
  if (!statusFields.length) return true;
  return statusFields.every((field) => normalizeFieldOptions(field.key_options).length > 0);
}

/**
 * Derives visual state for each setup step.
 *
 * @param {{
 *   hasFormId: boolean,
 *   hasCustomFields: boolean,
 *   statusConfigured: boolean,
 *   automationsConfigured: boolean,
 *   activeSection: string,
 * }} params
 * @returns {SetupStepState[]}
 */
export function computeSetupStepStates({
  hasFormId,
  hasCustomFields,
  statusConfigured,
  automationsConfigured,
  activeSection,
}) {
  const section = String(activeSection || '').toLowerCase();

  const overview = (() => {
    if (hasFormId) return 'complete';
    if (section === 'overview') return 'current';
    return 'upcoming';
  })();

  const fields = (() => {
    if (!hasFormId) return 'upcoming';
    if (hasCustomFields && statusConfigured) return 'complete';
    if (section === 'fields') return 'current';
    return 'pending';
  })();

  const automations = (() => {
    if (!hasFormId || !hasCustomFields) return 'upcoming';
    if (automationsConfigured) return 'complete';
    if (section === 'automations') return 'current';
    // Automations are optional — muted state, not "incomplete".
    return 'optional';
  })();

  return [overview, fields, automations];
}

/**
 * @param {SetupStepState} state
 */
export function isSetupStepComplete(state) {
  return state === 'complete';
}

/**
 * Connector after step `index` is complete only when both adjacent steps are done.
 *
 * @param {SetupStepState[]} states
 * @param {number} index
 */
export function isSetupConnectorComplete(states, index) {
  return isSetupStepComplete(states[index]) && isSetupStepComplete(states[index + 1]);
}
